import { Component, Input, OnChanges, ViewChild } from '@angular/core';
import { PoChartOptions, PoChartSerie, PoChartType, PoModalAction, PoModalComponent } from '@po-ui/ng-components';

import { ReviewStatsHotspot, ReviewStatsPeriodFilters } from '../../interfaces/review-stats.interface';
import { ReviewStatsService } from '../../review-stats.service';
import { integerAxis } from '../../utils/chart-axis.util';
import { ReviewStatsFileFindingsComponent } from '../review-stats-file-findings/review-stats-file-findings.component';

/** Quantos fontes o gráfico mostra antes do "Ver ranking completo". */
const TOP_LIMIT = 10;

/** Cores do card de risco (review-stats-severity), para as duas leituras combinarem. */
const SEVERITY_COLORS = { high: '#c64840', medium: '#f2b900', low: '#9da7a9' };

/**
 * Ranking de fontes em versão resumida: os mais críticos por severidade num gráfico de
 * barras, com o clique na barra levando aos achados do fonte. A grade completa — busca,
 * modo por categoria, paginação — abre num modal largo e só é montada quando ele abre.
 *
 * O modal de achados mora aqui, ao lado do modal do ranking, e não dentro da grade: um
 * modal aberto de dentro de outro herda o posicionamento dele. Lado a lado, o de achados
 * — declarado depois — abre por cima do ranking e, ao fechar, devolve a ele.
 */
@Component({
  selector: 'app-review-stats-hotspots-summary',
  templateUrl: './review-stats-hotspots-summary.component.html',
  styleUrls: ['./review-stats-hotspots-summary.component.css']
})
export class ReviewStatsHotspotsSummaryComponent implements OnChanges {
  @Input() filters: ReviewStatsPeriodFilters = {};
  @ViewChild(ReviewStatsFileFindingsComponent) fileFindings?: ReviewStatsFileFindingsComponent;
  @ViewChild('rankingModal', { static: true }) rankingModal?: PoModalComponent;

  readonly chartType = PoChartType.Bar;
  /** Monta a grade só enquanto o modal está aberto. */
  expanded = false;
  readonly closeAction: PoModalAction = { label: 'Fechar', action: () => this.collapse() };
  total = 0;
  categories: string[] = [];
  series: PoChartSerie[] = [];
  chartOptions: PoChartOptions = {};
  loading = false;
  loadError = false;

  /** Rótulo da barra → fonte, para o clique abrir os achados certos. */
  private sources = new Map<string, ReviewStatsHotspot>();
  private requestSequence = 0;

  constructor(private statsService: ReviewStatsService) {}

  ngOnChanges(): void {
    this.loading = true;
    this.loadError = false;
    const request = ++this.requestSequence;
    this.statsService.getHotspots({
      ...this.filters, mode: 'severity', page: 1, pageSize: TOP_LIMIT
    }).subscribe({
      next: page => {
        if (request !== this.requestSequence) { return; }
        this.build(page.items);
        this.total = page.total;
        this.loading = false;
      },
      error: () => {
        if (request !== this.requestSequence) { return; }
        this.loading = false;
        this.loadError = true;
      }
    });
  }

  get hasData(): boolean {
    return this.categories.length > 0;
  }

  /** Uma faixa por fonte, com folga para as três barras e o rótulo. */
  get chartHeight(): number {
    return Math.max(260, this.categories.length * 44 + 80);
  }

  get contextLabel(): string {
    const total = this.total.toLocaleString('pt-BR');
    return this.total > this.categories.length
      ? `Os ${this.categories.length} mais críticos de ${total} fontes com correções. Clique numa barra para ver os achados.`
      : `${total} ${this.total === 1 ? 'fonte' : 'fontes'} com correções. Clique numa barra para ver os achados.`;
  }

  get showAllLabel(): string {
    return `Ver ranking completo (${this.total.toLocaleString('pt-BR')} fontes)`;
  }


  expand(): void {
    this.expanded = true;
    this.rankingModal?.open();
  }

  collapse(): void {
    this.rankingModal?.close();
    this.expanded = false;
  }

  /** A grade do ranking pede os achados de um fonte. */
  showFindings(source: { path: string; language: string }): void {
    this.fileFindings?.open(source.path, source.language);
  }

  /** O modal de achados vai navegar para um review: o do ranking fecha junto. */
  onReviewOpened(): void {
    if (this.expanded) { this.collapse(); }
  }

  /** Em gráfico de barras o PO-UI emite a série clicada junto com a categoria do eixo. */
  onSeriesClick(event: PoChartSerie & { category?: string }): void {
    const source = this.sources.get(String(event?.category ?? ''));
    if (source) { this.fileFindings?.open(source.path, source.language); }
  }

  private build(items: ReviewStatsHotspot[]): void {
    this.sources.clear();
    const labels = items.map(item => this.uniqueLabel(item));
    this.categories = labels;
    this.series = [
      { label: 'Alta', data: items.map(i => i.severityHigh), color: SEVERITY_COLORS.high },
      { label: 'Média', data: items.map(i => i.severityMedium), color: SEVERITY_COLORS.medium },
      { label: 'Baixa', data: items.map(i => i.severityLow), color: SEVERITY_COLORS.low }
    ];
    this.chartOptions = integerAxis(items.flatMap(i => [i.severityHigh, i.severityMedium, i.severityLow]));
  }

  /** O nome do arquivo basta na barra; dois fontes com o mesmo nome ganham a pasta. */
  private uniqueLabel(item: ReviewStatsHotspot): string {
    const cut = Math.max(item.path.lastIndexOf('/'), item.path.lastIndexOf('\\'));
    const name = cut >= 0 ? item.path.slice(cut + 1) : item.path;
    let label = name;
    if (this.sources.has(label)) {
      const folder = cut > 0 ? item.path.slice(0, cut) : '';
      const parent = folder.slice(Math.max(folder.lastIndexOf('/'), folder.lastIndexOf('\\')) + 1);
      label = parent ? `${parent}/${name}` : item.path;
    }
    this.sources.set(label, item);
    return label;
  }
}
